import { FC, useState, useRef } from "react";
import { AppCSS, Lbl, TapButton } from "../../../../components";
import { ColDiv, RowDiv } from "../../../../components/misc/misc.styled";
import { CheckType, Docs, Payment, PayThrough, Progress, Project } from "../../../../redux/types";
import { toast } from "react-toastify";
import { PAYMENT_DETAILS_TABLE_HEADER } from "../../../../contants/dashboardHeader";
import PaymentDetailsTableHeaderCard from "../../../../components/table/CustomTableHeaderCard";
import PaymentDetailsDetailsTableBodyCard from "../../../../components/table/CustomTableBodyCard";
import { Box, Container, Drawer, Modal, Button, Typography } from "@mui/material";
import { LeadIcon } from "../../Leads/leads.styled";
import cancelIcon from "../../../../images/CancelIcon.png";
import { PAYMENT_DRAWER_DETAILS } from "../../../../contants/projectDetails";
import { rowDrawerDetails } from "../../../../types/type";
import DisplayLeadDetailsCard from "../../Leads/DisplayLeadDetailCard";
import { FileOpen } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useCreateProjectPayment, useCreateUploadUrl, useUpdateProjectPayment } from "../../../../apolloClient/hooks/general/createHook";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { uploadImage } from "../../../../redux/api/common";

interface PaymentProps {
  paymentDetails: Payment[];
  onFetchProject: Function;
  permissions?: boolean;
  project?: CustomerProjectDetails;
}

export const PaymentDetails: FC<PaymentProps> = ({
  paymentDetails,
  onFetchProject,
  permissions,
  project
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [createPaymentForm, setCreatePaymentForm] = useState({
    checkType: "",
    payThrough: "",
    amount: "",
    title: "",
    description: "",
    remarks: "",
    transactionId: ""
  });
  const [editPaymentForm, setEditPaymentForm] = useState({
    checkType: "",
    payThrough: "",
    amount: "",
    title: "",
    description: "",
    remarks: "",
    transactionId: ""
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const editFileRef = useRef<HTMLInputElement>(null);
  const [uploadUrl, setUploadUrl] = useState("");
  const [editUploadUrl, setEditUploadUrl] = useState("");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [editFileUrl, setEditFileUrl] = useState<string>("");
  const { createUploadUrl } = useCreateUploadUrl();
  const { createProjectPayment } = useCreateProjectPayment();
  const { updateProjectPayment } = useUpdateProjectPayment();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const handleDrawerOpen = (data: any) => {
    setSelectedRow(data);
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const handleCreatePaymentChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCreatePaymentForm({
      ...createPaymentForm,
      [e.target.name]: e.target.value
    });
  };

  const handleOpenCreateModal = () => setOpenCreateModal(true);
  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setIsSubmitting(false);
  };

  const handleOpenEditModal = (payment: Payment) => {
    setEditingPayment(payment);
    setEditPaymentForm({
      checkType: payment.paymentType || "",
      payThrough: payment.payThrough || "",
      amount: payment.amount.toString(),
      title: payment.title || "",
      description: payment.description || "",
      remarks: payment.remarks || "",
      transactionId: payment.transactionId || ""
    });
    setEditFileUrl(payment.Images || "");
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingPayment(null);
    setEditPaymentForm({
      checkType: "",
      payThrough: "",
      amount: "",
      title: "",
      description: "",
      remarks: "",
      transactionId: ""
    });
    setEditFileUrl("");
    setIsEditSubmitting(false);
  };

  const handleEditPaymentChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEditPaymentForm({
      ...editPaymentForm,
      [e.target.name]: e.target.value
    });
  };

  const onUpload = async () => {
    if (isSubmitting) return;
    
    const file = fileRef.current?.files[0];
    if (!file) {
      toast("An image have not been selected", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await uploadImage(uploadUrl, file);
      if (result.success) {
        await handleCreatePaymentSubmit();
      }
    } catch (error) {
      toast("Error uploading payment proof", {
        type: "error",
        position: "top-left",
        className: "toast-message",
      });
      setIsSubmitting(false);
    }
  };

  const handleCreatePaymentSubmit = async () => {
    const payload = {
      projectId: project?.projectId,
      checkType: createPaymentForm.checkType as CheckType,
      payThrough: createPaymentForm.payThrough as PayThrough,
      amount: Number(createPaymentForm.amount),
      title: createPaymentForm.title,
      description: createPaymentForm.description,
      remarks: createPaymentForm.remarks,
      transactionId: createPaymentForm.transactionId,
      images: fileUrl
    };
    try {
      await createProjectPayment(payload);
      toast.success("Payment created!");
      setOpenCreateModal(false);
      setCreatePaymentForm({
        checkType: "",
        payThrough: "",
        amount: "",
        title: "",
        description: "",
        remarks: "",
        transactionId: ""
      });
      setFileUrl("");
      setIsSubmitting(false);
      onFetchProject();
    } catch (err) {
      toast.error("Failed to create payment");
      setIsSubmitting(false);
    }
  };

  const getUploadUrl = async () => {
    try {
      const file = fileRef.current?.files[0];
      if (!file) return;
      const input = { contentType: file.type };
      const success = await createUploadUrl(input);
      if (success) {
        setUploadUrl(success.url);
        setFileUrl(success.fileName);
      } else {
        throw new Error("Failed to create Upload url.");
      }
    } catch (error) {
      toast("Error getting upload URL", { type: "error" });
    }
  };

  const getEditUploadUrl = async () => {
    try {
      const file = editFileRef.current?.files[0];
      if (!file) return;
      const input = { contentType: file.type };
      const success = await createUploadUrl(input);
      if (success) {
        setEditUploadUrl(success.url);
        setEditFileUrl(success.fileName);
      } else {
        throw new Error("Failed to create Upload url.");
      }
    } catch (error) {
      toast("Error getting upload URL", { type: "error" });
    }
  };

  const onEditUpload = async () => {
    if (isEditSubmitting) return;
    
    setIsEditSubmitting(true);
    const file = editFileRef.current?.files[0];
    if (file) {
      try {
        const result = await uploadImage(editUploadUrl, file);
        if (result.success) {
          await handleEditPaymentSubmit();
        } else {
          setIsEditSubmitting(false);
        }
      } catch (error) {
        toast("Error uploading payment proof", {
          type: "error",
          position: "top-left",
          className: "toast-message",
        });
        setIsEditSubmitting(false);
      }
    } else {
      await handleEditPaymentSubmit();
    }
  };

  const handleEditPaymentSubmit = async () => {
    if (!editingPayment) return;
    
    const payload = {
      Id: editingPayment.id,
      projectId: project?.projectId!,
      checkType: editPaymentForm.checkType as CheckType,
      payThrough: editPaymentForm.payThrough as PayThrough,
      amount: Number(editPaymentForm.amount),
      title: editPaymentForm.title,
      description: editPaymentForm.description,
      remarks: editPaymentForm.remarks,
      transactionId: editPaymentForm.transactionId,
      images: editFileUrl
    };
    try {
      await updateProjectPayment(payload);
      toast.success("Payment updated successfully!");
      setIsEditSubmitting(false);
      handleCloseEditModal();
      onFetchProject();
    } catch (err) {
      toast.error("Failed to update payment");
      setIsEditSubmitting(false);
    }
  };

  const handleShowImage = (image: string) => {
    if (image) window.open(image, "_blank");
  };

  const isImageFile = (url: string) => {
    return /\.(jpg|jpeg|png|gif)$/i.test(url);
  };

  const isPdfFile = (url: string) => {
    return /\.pdf$/i.test(url);
  };

  const showPaymentDetails = () => {
    return (
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            height: "100%",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
            overflowX: "hidden",
          },
        }}>
        <div
          onKeyDown={handleDrawerClose}
          style={{
            backgroundColor: AppCSS.WHITE,
            color: "black",
            height: "100%",
            display: "flex",
            width: "440px",
            flexDirection: "column",
            justifyContent: "start",
            alignItems: "start",
            padding: "20px",
            gap: "30px",
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "start",
              alignItems: "center",
              gap: "10px",
            }}>
            <LeadIcon
              src={cancelIcon}
              onClick={handleDrawerClose}
              style={{
                width: "12px",
                height: "12px",
                marginRight: "10px",
                cursor: "pointer",
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                width: "100px",
                marginRight: "10px",
                flexDirection: "row",
                alignItems: "center",
              }}>
              <Lbl
                title={`Payment Details`}
                size={20}
                width={80}
                color={AppCSS.GRAY_DARK}
                bold={600}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "center",
              width: "100%",
              paddingX: "20px",
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                width: "calc(100% - 20px)",
                marginRight: "20px",
                marginBottom: "20px",
              }}>
              {PAYMENT_DRAWER_DETAILS.map(
                (detail: rowDrawerDetails, index: number) => (
                  <DisplayLeadDetailsCard
                    key={`payment drawer details ${index}`}
                    label={detail.label}
                    value={
                      detail.isDate
                        ? selectedRow?.[detail.key]
                          ? new Date(
                              selectedRow?.[detail.key]
                            ).toLocaleDateString()
                          : ""
                        : selectedRow?.[detail.key]
                    }
                  />
                )
              )}
            </Box>
          </Box>
        </div>
      </Drawer>
    );
  };

  return (
    <Container>
      {/* Create Payment Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {permissions && (
          <Box
            onClick={handleOpenCreateModal}
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1,
              backgroundColor: "#3b82f6",
              color: "white",
              borderRadius: "8px",
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              "&:hover": { backgroundColor: "#2563eb" },
            }}
          >
            + Create Payment
          </Box>
        )}
      </Box>

      {/* Desktop Table View */}
      <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "12px", mt: 2 }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
          <Box component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
            <Box component="tr">
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Transaction ID</Box>
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Step</Box>
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Type</Box>
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Amount</Box>
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Approved</Box>
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Image</Box>
              <Box component="th" sx={{ py: 2, px: 2, fontSize: 13, fontWeight: 600, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>Actions</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {Array.isArray(paymentDetails) &&
              paymentDetails.map((item, index) => (
                <Box component="tr" key={`payment-${index}`} sx={{ borderTop: "1px solid #e5e7eb", "&:hover": { backgroundColor: "#f9fafb" } }}>
                  <Box component="td" sx={{ py: 2, px: 2, fontSize: 14, color: "#1f2937" }}>{item.transactionId}</Box>
                  <Box component="td" sx={{ py: 2, px: 2, fontSize: 14, color: "#6b7280" }}>{item.step}</Box>
                  <Box component="td" sx={{ py: 2, px: 2, fontSize: 14, color: "#6b7280" }}>{item.paymentType}</Box>
                  <Box component="td" sx={{ py: 2, px: 2, fontSize: 14, color: "#1f2937", fontWeight: 600 }}>${item.amount.toFixed(2)}</Box>
                  <Box component="td" sx={{ py: 2, px: 2 }}>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: item.approved ? "#dcfce7" : "#fee2e2",
                        color: item.approved ? "#16a34a" : "#dc2626",
                        borderRadius: "4px",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      {item.approved ? "Yes" : "No"}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ py: 2, px: 2 }}>
                    {item.Images && item.Images.length > 0 ? (
                      isImageFile(item.Images) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleShowImage(item.Images)}
                          sx={{ minWidth: 0, px: 1 }}
                        >
                          <img
                            src={item.Images}
                            alt="Payment"
                            style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4 }}
                          />
                        </Button>
                      ) : isPdfFile(item.Images) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleShowImage(item.Images)}
                          sx={{ minWidth: 0, px: 1, display: "flex", alignItems: "center" }}
                        >
                          <PictureAsPdfIcon sx={{ color: "#e53935", fontSize: 28 }} />
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleShowImage(item.Images)}
                          sx={{ minWidth: 0, px: 1, display: "flex", alignItems: "center" }}
                        >
                          <FileOpen sx={{ color: "#6b7280", fontSize: 24 }} />
                        </Button>
                      )
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 12 }}>No File</span>
                    )}
                  </Box>
                  <Box component="td" sx={{ py: 2, px: 2 }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Box
                        onClick={() => handleDrawerOpen(item)}
                        sx={{
                          px: 2,
                          py: 0.75,
                          backgroundColor: "#22c55e",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#16a34a" },
                        }}
                      >
                        View
                      </Box>
                      {permissions && (
                        <Box
                          onClick={() => handleOpenEditModal(item)}
                          sx={{
                            px: 2,
                            py: 0.75,
                            backgroundColor: "#3b82f6",
                            color: "white",
                            borderRadius: "6px",
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#2563eb" },
                          }}
                        >
                          Edit
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>

      {/* Mobile Card View */
      <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 2, mt: 2 }}>
        {Array.isArray(paymentDetails) && paymentDetails.map((item, index) => (
          <Box
            key={`payment-card-${index}`}
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#1f2937", mb: 0.5 }}>
                  {item.title || "Payment"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
                  {item.description || "No description"}
                </Typography>
              </Box>
              {item.Images && (
                <Box sx={{ ml: 1 }}>
                  {isImageFile(item.Images) ? (
                    <img
                      src={item.Images}
                      alt="Payment"
                      onClick={() => handleShowImage(item.Images)}
                      style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
                    />
                  ) : isPdfFile(item.Images) ? (
                    <Box
                      onClick={() => handleShowImage(item.Images)}
                      sx={{
                        width: 48,
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fee",
                        borderRadius: "6px",
                        cursor: "pointer"
                      }}
                    >
                      <PictureAsPdfIcon sx={{ color: "#e53935", fontSize: 28 }} />
                    </Box>
                  ) : (
                    <Box
                      onClick={() => handleShowImage(item.Images)}
                      sx={{
                        width: 48,
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f3f4f6",
                        borderRadius: "6px",
                        cursor: "pointer"
                      }}
                    >
                      <FileOpen sx={{ color: "#6b7280", fontSize: 24 }} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Type</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>{item.paymentType}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Amount</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#22c55e" }}>${item.amount.toFixed(2)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Pay Through</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>{item.payThrough}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Approved</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: item.approved ? "#22c55e" : "#ef4444" }}>
                  {item.approved ? "Yes" : "No"}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Box
                onClick={() => handleDrawerOpen(item)}
                sx={{
                  px: 2.5,
                  py: 0.75,
                  backgroundColor: "#22c55e",
                  color: "white",
                  borderRadius: "6px",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#16a34a" },
                }}
              >
                View
              </Box>
              {permissions && (
                <Box
                  onClick={() => handleOpenEditModal(item)}
                  sx={{
                    px: 2.5,
                    py: 0.75,
                    backgroundColor: "#3b82f6",
                    color: "white",
                    borderRadius: "6px",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#2563eb" },
                  }}
                >
                  Edit
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
}
      {showPaymentDetails()}

      {/* Create Payment Modal */}
      <Modal open={openCreateModal} onClose={handleCloseCreateModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: "95vw",
            maxWidth: 600,
            minWidth: 280,
            maxHeight: "90vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "stretch"
          }}
        >
          <Lbl title="Create Payment" size={20} bold={600} color={AppCSS.GRAY_DARK} />
          <form
            onSubmit={e => {
              e.preventDefault();
              onUpload();
            }}
            style={{ width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                width: "100%",
                flexWrap: "wrap"
              }}
            >
              <div style={{
                flex: 1,
                minWidth: 240,
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}>
                <select
                  value={createPaymentForm.checkType}
                  onChange={e => setCreatePaymentForm(prev => ({ ...prev, checkType: e.target.value }))}
                  required
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                >
                  <option value="" disabled>Select Check Type</option>
                  <option value="FIRST">First</option>
                  <option value="SECOND">Second</option>
                  <option value="PART_PAYMENT">Part Payment</option>
                  <option value="FINAL_INVOICE_PAYMENT">Final Invoice Payment</option>
                </select>
                <input
                  type="number"
                  name="amount"
                  value={createPaymentForm.amount}
                  onChange={handleCreatePaymentChange}
                  required
                  placeholder="Amount"
                  min={0}
                  step="0.01"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
                <input
                  type="text"
                  name="title"
                  value={createPaymentForm.title}
                  onChange={handleCreatePaymentChange}
                  placeholder="Title"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
                <input
                  type="text"
                  name="transactionId"
                  value={createPaymentForm.transactionId}
                  onChange={handleCreatePaymentChange}
                  placeholder="Transaction ID"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
              </div>
              <div style={{
                flex: 1,
                minWidth: 240,
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}>
                <select
                  value={createPaymentForm.payThrough}
                  onChange={e => setCreatePaymentForm(prev => ({ ...prev, payThrough: e.target.value }))}
                  required
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                >
                  <option value="" disabled>Select Pay Through</option>
                  <option value="CASH">Cash</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CARD">Card</option>
                  <option value="ACH">ACH</option>
                </select>
                <textarea
                  name="description"
                  value={createPaymentForm.description}
                  onChange={handleCreatePaymentChange}
                  placeholder="Description"
                  rows={3}
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb",
                    resize: "vertical"
                  }}
                />
                <input
                  type="text"
                  name="remarks"
                  value={createPaymentForm.remarks}
                  onChange={handleCreatePaymentChange}
                  placeholder="Remarks"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
              </div>
            </div>
            {/* Custom File Upload Section */}
            <div style={{ margin: "24px 0 0 0" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Upload Payment Proof
              </label>
              <div
                style={{
                  border: fileUrl.length > 0 ? "2px solid #10b981" : "2px dashed #d1d5db",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  backgroundColor: fileUrl.length > 0 ? "#ecfdf5" : "#f9fafb",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  marginTop: "8px"
                }}
                onClick={() => {
                  if (fileRef.current) fileRef.current.click();
                }}
                onMouseEnter={(e) => {
                  if (fileUrl.length === 0) {
                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                    e.currentTarget.style.borderColor = "#9ca3af";
                  }
                }}
                onMouseLeave={(e) => {
                  if (fileUrl.length === 0) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }
                }}
              >
                <input
                  type="file"
                  id="file"
                  ref={fileRef}
                  onChange={getUploadUrl}
                  accept="image/png, image/jpeg, image/jpg, application/pdf"
                  style={{ display: "none" }}
                />
                {fileUrl.length > 0 ? (
                  <div>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#10b981",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px auto",
                      }}
                    >
                      <FileOpen style={{ width: 24, height: 24, color: "white" }} />
                    </div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#059669" }}>
                      File Selected
                    </p>
                    <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                      Click to change file
                    </p>
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px auto",
                      }}
                    >
                      <FileOpen style={{ width: 24, height: 24, color: "#6b7280" }} />
                    </div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                      Choose a file
                    </p>
                    <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <Button onClick={handleCloseCreateModal} color="error" sx={{ fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ fontWeight: 700, px: 4 }}
                disabled={!createPaymentForm.amount || !fileUrl || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        </Box>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal open={openEditModal} onClose={handleCloseEditModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: "95vw",
            maxWidth: 600,
            minWidth: 280,
            maxHeight: "90vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "stretch"
          }}
        >
          <Lbl title="Edit Payment" size={20} bold={600} color={AppCSS.GRAY_DARK} />
          <form
            onSubmit={e => {
              e.preventDefault();
              onEditUpload();
            }}
            style={{ width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                width: "100%",
                flexWrap: "wrap"
              }}
            >
              <div style={{
                flex: 1,
                minWidth: 240,
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}>
                <select
                  value={editPaymentForm.checkType}
                  onChange={e => setEditPaymentForm(prev => ({ ...prev, checkType: e.target.value }))}
                  required
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                >
                  <option value="" disabled>Select Check Type</option>
                  <option value="FIRST">First</option>
                  <option value="SECOND">Second</option>
                  <option value="PART_PAYMENT">Part Payment</option>
                  <option value="FINAL_INVOICE_PAYMENT">Final Invoice Payment</option>
                </select>
                <input
                  type="number"
                  name="amount"
                  value={editPaymentForm.amount}
                  onChange={handleEditPaymentChange}
                  required
                  placeholder="Amount"
                  min={0}
                  step="0.01"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
                <input
                  type="text"
                  name="title"
                  value={editPaymentForm.title}
                  onChange={handleEditPaymentChange}
                  placeholder="Title"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
                <input
                  type="text"
                  name="transactionId"
                  value={editPaymentForm.transactionId}
                  onChange={handleEditPaymentChange}
                  placeholder="Transaction ID"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
              </div>
              <div style={{
                flex: 1,
                minWidth: 240,
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}>
                <select
                  value={editPaymentForm.payThrough}
                  onChange={e => setEditPaymentForm(prev => ({ ...prev, payThrough: e.target.value }))}
                  required
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                >
                  <option value="" disabled>Select Pay Through</option>
                  <option value="CASH">Cash</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CARD">Card</option>
                  <option value="ACH">ACH</option>
                </select>
                <textarea
                  name="description"
                  value={editPaymentForm.description}
                  onChange={handleEditPaymentChange}
                  placeholder="Description"
                  rows={3}
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb",
                    resize: "vertical"
                  }}
                />
                <input
                  type="text"
                  name="remarks"
                  value={editPaymentForm.remarks}
                  onChange={handleEditPaymentChange}
                  placeholder="Remarks"
                  style={{
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "15px",
                    background: "#f9fafb"
                  }}
                />
              </div>
            </div>
            {/* Custom File Upload Section */}
            <div style={{ margin: "24px 0 0 0" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Upload Payment Proof {editingPayment?.Images && "(Optional - leave empty to keep current)" }
              </label>
              <div
                style={{
                  border: editFileUrl.length > 0 ? "2px solid #10b981" : "2px dashed #d1d5db",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  backgroundColor: editFileUrl.length > 0 ? "#ecfdf5" : "#f9fafb",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  marginTop: "8px"
                }}
                onClick={() => {
                  if (editFileRef.current) editFileRef.current.click();
                }}
                onMouseEnter={(e) => {
                  if (editFileUrl.length === 0) {
                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                    e.currentTarget.style.borderColor = "#9ca3af";
                  }
                }}
                onMouseLeave={(e) => {
                  if (editFileUrl.length === 0) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }
                }}
              >
                <input
                  type="file"
                  id="editFile"
                  ref={editFileRef}
                  onChange={getEditUploadUrl}
                  accept="image/png, image/jpeg, image/jpg, application/pdf"
                  style={{ display: "none" }}
                />
                {editFileUrl.length > 0 ? (
                  <div>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#10b981",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px auto",
                      }}
                    >
                      <FileOpen style={{ width: 24, height: 24, color: "white" }} />
                    </div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#059669" }}>
                      File Selected
                    </p>
                    <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                      Click to change file
                    </p>
                  </div>
                ) : editingPayment?.Images ? (
                  <div>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#3b82f6",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px auto",
                      }}
                    >
                      <FileOpen style={{ width: 24, height: 24, color: "white" }} />
                    </div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                      Current File Exists
                    </p>
                    <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                      Click to upload new file
                    </p>
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px auto",
                      }}
                    >
                      <FileOpen style={{ width: 24, height: 24, color: "#6b7280" }} />
                    </div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                      Choose a file (optional)
                    </p>
                    <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <Button onClick={handleCloseEditModal} color="error" sx={{ fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ fontWeight: 700, px: 4 }}
                disabled={!editPaymentForm.amount || isEditSubmitting}
              >
                {isEditSubmitting ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        </Box>
      </Modal>
    </Container>
  );
};
